
/**
 * RainTimeline — rain_timing evidence component.
 *
 * One horizontal 24-hour track per day, shading rain_window_start to
 * rain_window_end, with a marker at peak_precip_hour. Hours are 0-23
 * local to the park. Axis labelled with clock times, not hour numbers.
 *
 * This panel distinguishes an afternoon thunderstorm from all-day rain
 * at the same daily probability.
 *
 * Spec references: §7 (rain_timing).
 */

/** Hour number → clock label: 0 → "12 AM", 14 → "2 PM", etc. */
const hourToClock = (h) => {
  if (h == null) return "";
  const hr = ((h % 24) + 24) % 24; // normalise
  if (hr === 0) return "12 AM";
  if (hr === 12) return "12 PM";
  return hr < 12 ? `${hr} AM` : `${hr - 12} PM`;
};

/** Short date — "Aug 12" */
const shortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Day of week — "Tue" */
const dayOfWeek = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short" });
};

// ─── Axis tick marks — every 6 hours: 12 AM, 6 AM, 12 PM, 6 PM ──────────────────
const TICKS = [0, 6, 12, 18];

const RainTimeline = ({ panel }) => {
  const f = panel.facts || {};
  const days = f.days || [];

  if (days.length === 0) return null;

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700/50">
      {days.map((day) => {
        const startPct = (day.rain_window_start / 24) * 100;
        const endPct = (day.rain_window_end / 24) * 100;
        const windowWidth = endPct - startPct;
        const hasPeak = day.peak_precip_hour != null;
        const peakPct = hasPeak ? (day.peak_precip_hour / 24) * 100 : null;

        return (
          <div key={day.date} className="py-3 first:pt-0 last:pb-0">
            {/* Header row: date + summary */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {dayOfWeek(day.date)} {shortDate(day.date)}
                </span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                  {day.precip_probability_pct}% rain
                </span>
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {day.daylight_rain_hours} daylight rain hrs
              </span>
            </div>

            {/* Timeline track */}
            <div className="relative h-5 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
              {/* Rain window shade */}
              <div
                className="absolute top-0 bottom-0 bg-blue-300/50 dark:bg-blue-700/40 rounded"
                style={{
                  left: `${startPct}%`,
                  width: `${windowWidth}%`,
                }}
              />

              {/* Peak precip marker — only when present */}
              {hasPeak && (
                <>
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-blue-600 dark:bg-blue-400"
                    style={{ left: `${peakPct}%` }}
                  />
                  <div
                    className="absolute -top-0.5 w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full -translate-x-1/2"
                    style={{ left: `${peakPct}%` }}
                  />
                </>
              )}
            </div>

            {/* Axis labels */}
            <div className="relative h-4 mt-0.5">
              {TICKS.map((tick) => (
                <span
                  key={tick}
                  className="absolute text-[11px] text-gray-400 dark:text-gray-500 -translate-x-1/2"
                  style={{ left: `${(tick / 24) * 100}%` }}
                >
                  {hourToClock(tick)}
                </span>
              ))}
            </div>

            {/* Window detail — as chips for clear separation */}
            <div className="flex items-center gap-2 mt-2">
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8a5 5 0 0 0-9.6-1.5A4 4 0 1 0 7 15h11a3 3 0 0 0 0-6z" />
                </svg>
                {hourToClock(day.rain_window_start)} – {hourToClock(day.rain_window_end)}
              </span>
              {hasPeak && (
                <>
                  <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <svg className="w-3 h-3 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 7v1M12 16v1M7 12h1M16 12h1" />
                    </svg>
                    Peak at {hourToClock(day.peak_precip_hour)}
                  </span>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RainTimeline;